import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import KrillChat from 'krill';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';
import intl from 'react-intl-universal';
import { zIndexValues } from 'utils/styles';
import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';

const propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setIssueShareConfirmModalOpen: PropTypes.func.isRequired,
  shareIssueConfirmModalBodyText: PropTypes.any,
  shareIssueConfirmFunc: PropTypes.any,
  shareIssueCancelFunc: PropTypes.any,
  shareIssueServiceType: PropTypes.string,
};

const log = logger.createLogger('ShareIssueButtonMenu');

const ShareIssueButtonMenu = ({
  anchorEl,
  open,
  onClose,
  setIssueShareConfirmModalOpen,
  shareIssueConfirmModalBodyText,
  shareIssueConfirmFunc,
  shareIssueCancelFunc,
  shareIssueServiceType,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [conversationList, setConversationList] = useState([]);
  const origin = window.location.origin;
  const path = window.location.pathname;
  const pathList = path.split('/');
  const issueId = pathList[pathList.length - 1];

  const linkPrefix = `${origin}${
    import.meta.env.VITE_ROUTE_BASENAME
  }dashboard/browse/issues`;

  let shareLink = `${linkPrefix}/${issueId}?serviceType=${shareIssueServiceType}`;

  const context = KrillChat.Context.useKrillChatContext();
  const { conversations } = context;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user info
        const userInfoResponse = await KrillChat.API.getUsersInfo(
          conversations.map((conversation) => conversation.userID)
        );

        const requestVariables = {
          params: {
            userId: localData.get(USER_DATA.USER_ID),
            organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          },
        };

        const usersInOrganization = await api.get(
          DOLPHIN_API.USERS_IN_ORGANIZATION,
          requestVariables
        );

        const parsedUserInfo = JSON.parse(userInfoResponse.data);
        // Convert array to object with userID as key for easier lookup
        const userInfoMap = parsedUserInfo.reduce((acc, user) => {
          acc[user.publicInfo.userID] = user.publicInfo;
          return acc;
        }, {});

        // Map conversations to the required format
        const mappedConversations = conversations.map((conversation) => ({
          id: conversation.conversationID,
          userID: conversation.userID,
          groupID: conversation.groupID,
          showName: conversation.showName,
          nickname:
            userInfoMap[conversation.userID]?.nickname || conversation.showName,
        }));

        const filteredConversations = mappedConversations.filter(
          (conversation) =>
            usersInOrganization.users.some(
              (user) => user.username === conversation.userID
            )
        );

        setConversationList(filteredConversations);
      } catch (error) {
        log.error('Error fetching conversation data', error);
      }
    };
    fetchData();
  }, []);

  const handleClickShareLink = async (conversation) => {
    shareIssueConfirmModalBodyText.current = conversation.nickname;
    shareIssueConfirmFunc.current = createConfirmHandler(conversation);
    shareIssueCancelFunc.current = handleCancel;
    setIssueShareConfirmModalOpen(true);
    onClose();
  };

  // Create a new function that will be used as the ref
  const createConfirmHandler = (conversation) => {
    return async () => {
      const link = shareLink;
      await KrillChat.API.sendMessage(
        link,
        'text',
        conversation.userID,
        conversation.groupID
      );

      setIssueShareConfirmModalOpen(false);
      enqueueSnackbar(intl.get('issue_details_shared_completed'), {
        variant: 'success',
      });
    };
  };

  const handleCancel = async () => {
    setIssueShareConfirmModalOpen(false);
    onClose();
  };

  return (
    <Menu
      id="share-issue-button-menu"
      anchorEl={anchorEl}
      open={open}
      sx={{ zIndex: zIndexValues.modalMenu }}
      onClose={onClose}>
      {conversationList.length > 0 ? (
        conversationList.map((conversation, index) => {
          return (
            <MenuItem
              key={index}
              onClick={() => {
                handleClickShareLink(conversation, shareLink);
              }}>
              {conversation.nickname}
            </MenuItem>
          );
        })
      ) : (
        <MenuItem disabled>{intl.get('issue_details_share_no_users')}</MenuItem>
      )}
    </Menu>
  );
};

ShareIssueButtonMenu.propTypes = propTypes;

export default ShareIssueButtonMenu;
